/**
 * Pieces the three ledger-driven reports share — the trial balance
 * (`./trialBalance`), the cash-flow statement (`./cashFlow`) and the debtor /
 * creditor lists (`./openItems`). All three are native-currency documents: one
 * block per currency, never a sum across currencies (plans/financial-reports.md
 * "Currency: native currency per block, no translation in v1").
 *
 * Kept tiny and pure on purpose: each rule here exists once so the three
 * builders cannot drift on it.
 */
import { z } from "zod";
import { BALANCE_SHEET_TOLERANCE } from "./balanceSheet/model.js";
/**
 * Under this, a difference is float noise rather than an imbalance — the same
 * 0.01 the balance sheet's verdict uses (`BALANCE_SHEET_TOLERANCE`), so every
 * statement says "balanced" on the same terms.
 */
export const LEDGER_REPORT_TOLERANCE = BALANCE_SHEET_TOLERANCE;
/** Whether two amounts agree within {@link LEDGER_REPORT_TOLERANCE}. */
export function amountsAgree(a, b) {
    return Math.abs(a - b) < LEDGER_REPORT_TOLERANCE;
}
/**
 * Round to cents. Every amount a ledger report prints passes through here once,
 * at the build step, so the totals the template shows are the sums of the
 * figures it shows. `-0` is normalised to `0` (it would otherwise print as
 * "-0.00" and fail `toEqual(0)` in a golden test).
 */
export function roundCents(amount) {
    const rounded = Math.round(amount * 100) / 100;
    return rounded === 0 ? 0 : rounded;
}
/** Σ of a list of amounts, rounded to cents. */
export function sumCents(amounts) {
    return roundCents(amounts.reduce((sum, amount) => sum + amount, 0));
}
/**
 * The block order of every ledger report: the org's own currency first, the
 * others alphabetically, and the `null` block (legs no currency could be
 * attributed to — see `ledger_entry_report_currency`) last. Invariant I18.
 */
export function compareReportCurrencies(baseCurrency) {
    return (a, b) => {
        if (a === b)
            return 0;
        if (a === null)
            return 1;
        if (b === null)
            return -1;
        if (a === baseCurrency)
            return -1;
        if (b === baseCurrency)
            return 1;
        return a.localeCompare(b);
    };
}
/**
 * A Postgres `numeric` off PostgREST. It arrives as a JSON number, but a
 * coercing parse keeps a string-encoded one (a future `::text` cast, a large
 * value) from turning a sum into string concatenation.
 */
export const pgNumericSchema = z.coerce.number();
/** The fund filter a ledger report was drawn with (the P&L shapes). */
export const ledgerReportFundFilterSchema = z.object({
    /** Funds the legs were filtered to; empty = no fund filter. */
    fundIds: z.array(z.string()),
    /** Only legs with no fund (`ledger_entries.fund_id IS NULL`). */
    unallocatedOnly: z.boolean(),
});
/**
 * Whether a fund filter narrows the legs. When it does, a journal can lose some
 * of its legs (the bank leg has no fund, the expense leg does), so the report
 * shows a labelled fund-tagging difference rather than a false imbalance.
 */
export function isFundFiltered(filter) {
    return filter.fundIds.length > 0 || filter.unallocatedOnly;
}
/** The `p_fund_ids` / `p_unallocated_only` RPC arguments for a fund filter. */
export function fundFilterRpcArgs(filter) {
    return {
        p_fund_ids: filter.fundIds.length > 0 ? filter.fundIds : undefined,
        p_unallocated_only: filter.unallocatedOnly,
    };
}
/** A reporting period, both ends `YYYY-MM-DD` and inclusive. */
export const ledgerReportPeriodSchema = z.object({
    startDate: z.string(),
    endDate: z.string(),
});
//# sourceMappingURL=ledgerReports.js.map